import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Dropdown, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { ChevronDownIcon } from 'lucide-react';

export type SentimentKey = 'positive' | 'negative' | 'neutral';

export interface RawSentimentItem {
  id: SentimentKey;
  title: string;
  count: number;
}

export interface RawSentimentSection {
  type: 'section';
  label: string;
  items: RawSentimentItem[];
}

export interface SentimentItem {
  id: SentimentKey;
  title: string;
  count: string;
}

export interface SentimentSection {
  type: 'section';
  label: string;
  items: SentimentItem[];
}

export interface GetSentimentStateCountsRes {
  data: {
    is_success: boolean;
    message?: string;
    messageTypeId?: string;
    page_count?: number;
    status?: number;
    data: RawSentimentSection[];
  };
}

type SentimentFilterMode = 'dropdown' | 'pill' | 'both';

interface SentimentFilterProps {
  selectedSentiments: SentimentKey[];
  onChange: (selected: SentimentKey[]) => void;
  sections: RawSentimentSection[];
  mode?: SentimentFilterMode;
}

const SentimentFilter: React.FC<SentimentFilterProps> = ({
  selectedSentiments,
  onChange,
  sections,
  mode = 'both',
}) => {
  const [mainDropdownOpen, setMainDropdownOpen] = useState(false);
  const [pillDropdownOpen, setPillDropdownOpen] = useState(false);

  const [tempSelected, setTempSelected] =
    useState<SentimentKey[]>(selectedSentiments);

  useEffect(() => {
    if (mainDropdownOpen || pillDropdownOpen) {
      setTempSelected(selectedSentiments);
    }
  }, [mainDropdownOpen, pillDropdownOpen, selectedSentiments]);

  const sentimentOptions: SentimentSection[] = useMemo(() => {
    return (sections ?? []).map((s) => ({
      type: 'section',
      label: String(s?.label ?? ''),
      items: (s?.items ?? []).map((it) => ({
        id: it.id,
        title: String(it?.title ?? ''),
        count: String(it?.count ?? '0'),
      })),
    }));
  }, [sections]);

  const allItems = useMemo(
    () => sentimentOptions.flatMap((s) => s.items),
    [sentimentOptions]
  );

  const handleSelect = (id: SentimentKey, checked: boolean) => {
    setTempSelected((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((x) => x !== id);
    });
  };

  const handleApply = () => {
    onChange(tempSelected);
    setMainDropdownOpen(false);
    setPillDropdownOpen(false);
  };

  const handleReset = () => {
    onChange([]);
    setTempSelected([]);
    setMainDropdownOpen(false);
    setPillDropdownOpen(false);
  };

  const menuContent = (
    <div
      style={{
        backgroundColor: 'var(--surface-elevated)',
        width: 240,
        borderRadius: 12,
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        padding: '12px 16px',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {sentimentOptions.map((section) => (
        <div key={section.label} style={{ marginBottom: 8 }}>
          {section.items.map((item) => (
            <div key={item.id} style={{ padding: '4px 0' }}>
              <Checkbox
                checked={tempSelected.includes(item.id)}
                onChange={(e) => handleSelect(item.id, e.target.checked)}
                className='custom-menu-shipment-checkbox-only checkbox-sm'
                style={{ fontSize: 12 }}
              >
                {item.title} ({item.count})
              </Checkbox>
            </div>
          ))}
        </div>
      ))}

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 12,
          gap: 8,
        }}
      >
        <Button
          type='text'
          onClick={handleReset}
          style={{ fontSize: 12, height: 28, width: 52, fontWeight: 600 }}
        >
          Reset
        </Button>

        <Button
          type='primary'
          onClick={handleApply}
          style={{
            fontWeight: 600,
            height: 28,
            width: 58,
            fontSize: 12,
            borderRadius: 8,
            backgroundColor: 'var(--text-primary)',
            borderColor: 'var(--text-primary)',
            color: 'var(--surface-base)',
          }}
        >
          Apply
        </Button>
      </div>
    </div>
  );

  const firstSelected = selectedSentiments[0];
  const firstTitle =
    allItems.find((o) => o.id === firstSelected)?.title ?? firstSelected;

  const showDropdown = mode === 'both' || mode === 'dropdown';
  const showPill = mode === 'both' || mode === 'pill';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {showDropdown && (
        <div className='ai-state-filter'>
          <Dropdown
            dropdownRender={() => menuContent}
            trigger={['click']}
            open={mainDropdownOpen}
            onOpenChange={(flag) => {
              setMainDropdownOpen(flag);
              if (flag) setPillDropdownOpen(false);
            }}
          >
            <button
              className='zl-btn zl-btn--10-600 rounded-full flex-center'
              type='button'
            >
              Sentiment <ChevronDownIcon size={10} />
            </button>
          </Dropdown>
        </div>
      )}

      {showPill && selectedSentiments.length > 0 && (
        <Dropdown
          dropdownRender={() => menuContent}
          trigger={['click']}
          open={pillDropdownOpen}
          onOpenChange={(flag) => {
            setPillDropdownOpen(flag);
            if (flag) setMainDropdownOpen(false);
          }}
        >
          <button
            className='zl-btn zl-btn-clr rounded-full flex-center'
            style={{ whiteSpace: 'nowrap', gap: 6 }}
            type='button'
          >
            Sentiment: {firstTitle}
            {selectedSentiments.length > 1
              ? ` +${selectedSentiments.length - 1}`
              : ''}
            <span
              className='ai-state-close'
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
            >
              <CloseOutlined />
            </span>
          </button>
        </Dropdown>
      )}
    </div>
  );
};

export default SentimentFilter;
